import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralWithScope_UserWithoutId } from '../../helpers/DataStructures';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import { translateFromStatusToShortTextForOfferer } from '../../helpers/Language';
import { getReferralUnreadCount } from '../../helpers/referralUnread';
import Busy from '../../components/Busy';
import ReferralUnreadBadge from './ReferralUnreadBadge';
import ReferralRouteShell from './ReferralRouteShell';

const ReferralsReceivedPage: React.FC = () => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backTo = `${location.pathname}${location.search}${location.hash}`;

  const { isLoading, isError, data } = useQuery(
    [CacheKeys.REFERRALS_TO_USER],
    () => apiServiceInstance.getUserReferralsIn(),
    { keepPreviousData: true }
  );

  return (
    <ReferralRouteShell title="Referrals Received" backTo="/dashboard">
      {isLoading || data === undefined ? (
        <Busy message="Loading referrals..." />
      ) : isError ? (
        <Typography>Unable to load received referrals.</Typography>
      ) : data.length === 0 ? (
        <Typography>No referrals received yet</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {data.map((referral: ReferralWithScope_UserWithoutId) => (
                <TableRow
                  key={referral.id}
                  onClick={() =>
                    navigate(`/dashboard/referrals/in/${referral.id}`, {
                      // REQ-019: referral details should return to the actual originating list when entered in-app.
                      state: { backTo },
                    })
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{referral.user.givenName} {referral.user.familyName}</TableCell>
                  <TableCell>{DateTimeFormat(referral.created)}</TableCell>
                  <TableCell>{referral.theReferralName}</TableCell>
                  <TableCell>
                    <Stack spacing={0.75} alignItems="flex-start">
                      <ReferralUnreadBadge count={getReferralUnreadCount(referral)} />
                      <Typography component="span" variant="inherit">
                        {translateFromStatusToShortTextForOfferer(referral.status)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/dashboard/referrals/in/${referral.id}`, {
                            state: { backTo },
                          });
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ReferralRouteShell>
  );
};

export default ReferralsReceivedPage;
